import React, { useEffect, useState } from 'react';
import apiClient from '../../services/apiClient';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Alert,
  Divider,
  Card,
  CardContent,
  CardActions,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  PhoneIphone as MobileIcon,
  Build as MaintenanceIcon,
  Update as UpdateIcon,
  Speed as PerformanceIcon,
  Security as SecurityIcon,
  Tune as ConfigIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  RestartAlt as RestartIcon,
  Delete as ClearIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Schedule as ScheduleIcon,
  Block as BlockIcon,
  CheckCircleOutline as EnableIcon,
  Cancel as DisableIcon,
  PowerSettingsNew as PowerIcon,
} from '@mui/icons-material';

interface FeatureFlag {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  category: 'core' | 'experimental' | 'beta' | 'deprecated';
}

interface MaintenanceWindow {
  id: string;
  title: string;
  startTime: string;
  endTime: string;
  description: string;
  active: boolean;
}

const MobileAppSettingsPage: React.FC = () => {
  const theme = useTheme();
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  // Forced-update policy (GET/PUT /api/v1/admin/settings/mobile-app-version):
  // app builds older than this version are blocked until they update
  const [forceUpdateVersion, setForceUpdateVersion] = useState('');
  const [latestAppVersion, setLatestAppVersion] = useState('');
  const [versionStatus, setVersionStatus] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [savingVersion, setSavingVersion] = useState(false);

  useEffect(() => {
    apiClient.get('/api/v1/admin/settings/mobile-app-version')
      .then(response => {
        const data = response.data?.data;
        if (data) {
          setForceUpdateVersion(data.minimumVersion || '');
          setLatestAppVersion(data.latestVersion || '');
        }
      })
      .catch(() => setVersionStatus({ type: 'error', text: 'Could not load the current minimum version' }));
  }, []);

  const saveForceUpdateVersion = async () => {
    const version = forceUpdateVersion.trim();
    if (!/^\d+(\.\d+){0,2}$/.test(version)) {
      setVersionStatus({ type: 'error', text: 'Enter a version like 3.1.0' });
      return;
    }
    setSavingVersion(true);
    setVersionStatus(null);
    try {
      const newer = (a: string, b: string) => {
        const pa = a.split('.').map(Number); const pb = b.split('.').map(Number);
        for (let i = 0; i < 3; i++) { if ((pa[i] || 0) !== (pb[i] || 0)) return (pa[i] || 0) > (pb[i] || 0); }
        return false;
      };
      const latest = !latestAppVersion || newer(version, latestAppVersion) ? version : latestAppVersion;
      await apiClient.put('/api/v1/admin/settings/mobile-app-version', { minimumVersion: version, latestVersion: latest });
      setLatestAppVersion(latest);
      setVersionStatus({ type: 'success', text: `App versions below ${version} must now update` });
    } catch (error: any) {
      setVersionStatus({ type: 'error', text: error?.response?.data?.error || 'Failed to save the minimum version' });
    } finally {
      setSavingVersion(false);
    }
  };
  const [apiRateLimit, setApiRateLimit] = useState(1000);
  const [maxConcurrentUsers, setMaxConcurrentUsers] = useState(10000);
  const [dialogOpen, setDialogOpen] = useState('');
  
  const [featureFlags, setFeatureFlags] = useState<FeatureFlag[]>([
    {
      id: 'yacht-booking',
      name: 'Yacht Booking',
      description: 'Enable yacht rental functionality',
      enabled: true,
      category: 'core',
    },
    {
      id: 'driver-hiring',
      name: 'Driver Hiring',
      description: 'Allow users to hire personal drivers',
      enabled: true,
      category: 'core',
    },
    {
      id: 'biometric-auth',
      name: 'Biometric Authentication',
      description: 'Face ID / Fingerprint login',
      enabled: true,
      category: 'beta',
    },
    {
      id: 'ai-recommendations',
      name: 'AI Recommendations',
      description: 'Smart vehicle and driver suggestions',
      enabled: false,
      category: 'experimental',
    },
    {
      id: 'trip-sharing',
      name: 'Trip Sharing',
      description: 'Share trip status with contacts',
      enabled: true,
      category: 'beta',
    },
    {
      id: 'loyalty-program',
      name: 'Loyalty Program',
      description: 'Points and rewards system',
      enabled: false,
      category: 'experimental',
    },
  ]);

  const [maintenanceWindows] = useState<MaintenanceWindow[]>([
    {
      id: '1',
      title: 'Weekly System Updates',
      startTime: '02:00',
      endTime: '04:00',
      description: 'Automatic system updates every Sunday',
      active: true,
    },
    {
      id: '2',
      title: 'Database Optimization',
      startTime: '01:00',
      endTime: '02:00',
      description: 'Monthly database maintenance (1st of each month)',
      active: true,
    },
  ]);

  const handleFeatureToggle = (featureId: string) => {
    setFeatureFlags(prev =>
      prev.map(feature =>
        feature.id === featureId
          ? { ...feature, enabled: !feature.enabled }
          : feature
      )
    );
  };

  const handleEmergencyAction = (action: string) => {
    setDialogOpen(action);
  };

  const confirmEmergencyAction = (action: string) => {
    setDialogOpen('');
    // Here you would call the appropriate API
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'core':
        return 'success';
      case 'beta':
        return 'warning';
      case 'experimental':
        return 'info';
      case 'deprecated':
        return 'error';
      default:
        return 'default';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'core':
        return <CheckIcon />;
      case 'beta':
        return <WarningIcon />;
      case 'experimental':
        return <ConfigIcon />;
      case 'deprecated':
        return <ErrorIcon />;
      default:
        return <SettingsIcon />;
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Mobile App Controls
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage mobile app features, performance, and emergency controls
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* System Status */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <MobileIcon color="primary" />
                <Typography variant="h6">System Status</Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Alert severity={maintenanceMode ? 'warning' : 'success'} sx={{ mb: 2 }}>
                  Mobile app is currently {maintenanceMode ? 'in maintenance mode' : 'operational'}
                </Alert>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={maintenanceMode}
                      onChange={(e) => setMaintenanceMode(e.target.checked)}
                      color="warning"
                    />
                  }
                  label="Maintenance Mode"
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Force Update Version
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  App builds older than this version are blocked with an update screen and can't make payments.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    value={forceUpdateVersion}
                    onChange={(e) => setForceUpdateVersion(e.target.value)}
                    placeholder="e.g., 3.1.0"
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<UpdateIcon />}
                    onClick={saveForceUpdateVersion}
                    disabled={savingVersion}
                  >
                    {savingVersion ? 'Saving…' : 'Set'}
                  </Button>
                </Box>
                {versionStatus && (
                  <Alert severity={versionStatus.type} sx={{ mt: 1 }}>
                    {versionStatus.text}
                  </Alert>
                )}
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  API Rate Limit (requests/minute)
                </Typography>
                <Box sx={{ px: 2 }}>
                  <Slider
                    value={apiRateLimit}
                    onChange={(e, value) => setApiRateLimit(value as number)}
                    min={100}
                    max={2000}
                    step={100}
                    valueLabelDisplay="auto"
                    marks={[
                      { value: 500, label: '500' },
                      { value: 1000, label: '1000' },
                      { value: 1500, label: '1500' },
                    ]}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Emergency Controls */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderColor: theme.palette.error.main, borderWidth: 1 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <WarningIcon color="error" />
                <Typography variant="h6" color="error">
                  Emergency Controls
                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Use these controls only in emergency situations
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<BlockIcon />}
                  onClick={() => handleEmergencyAction('killswitch')}
                  fullWidth
                >
                  Emergency Kill Switch
                </Button>
                
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<RestartIcon />}
                  onClick={() => handleEmergencyAction('restart')}
                  fullWidth
                >
                  Force App Restart (All Users)
                </Button>
                
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<ClearIcon />}
                  onClick={() => handleEmergencyAction('clear-cache')}
                  fullWidth
                >
                  Clear All User Caches
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Feature Flags */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <ConfigIcon color="primary" />
                <Typography variant="h6">Feature Flags</Typography>
                <Typography variant="caption" color="text.secondary">
                  Enable/disable mobile app features remotely
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                {featureFlags.map((feature) => (
                  <Grid item xs={12} md={6} key={feature.id}>
                    <Paper
                      sx={{
                        p: 2,
                        border: 1,
                        borderColor: feature.enabled ? 'success.main' : 'divider',
                        backgroundColor: feature.enabled
                          ? alpha(theme.palette.success.main, 0.05)
                          : 'background.paper',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="subtitle1" fontWeight="medium">
                              {feature.name}
                            </Typography>
                            <Chip
                              icon={getCategoryIcon(feature.category)}
                              label={feature.category}
                              size="small"
                              color={getCategoryColor(feature.category) as any}
                              variant="outlined"
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {feature.description}
                          </Typography>
                        </Box>
                        
                        <Switch
                          checked={feature.enabled}
                          onChange={() => handleFeatureToggle(feature.id)}
                          color={feature.category === 'experimental' ? 'warning' : 'primary'}
                        />
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Maintenance Windows */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <ScheduleIcon color="primary" />
                <Typography variant="h6">Maintenance Windows</Typography>
              </Box>
              
              <List dense>
                {maintenanceWindows.map((window) => (
                  <ListItem key={window.id} sx={{ px: 0 }}>
                    <ListItemIcon>
                      <MaintenanceIcon color={window.active ? 'primary' : 'disabled'} />
                    </ListItemIcon>
                    <ListItemText
                      primary={window.title}
                      secondary={`${window.startTime} - ${window.endTime} | ${window.description}`}
                    />
                    <ListItemSecondaryAction>
                      <Switch checked={window.active} size="small" />
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
              
              <Button variant="outlined" size="small" sx={{ mt: 2 }} fullWidth>
                Add Maintenance Window
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Performance Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <PerformanceIcon color="primary" />
                <Typography variant="h6">Performance Settings</Typography>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Max Concurrent Users
                </Typography>
                <Box sx={{ px: 2 }}>
                  <Slider
                    value={maxConcurrentUsers}
                    onChange={(e, value) => setMaxConcurrentUsers(value as number)}
                    min={1000}
                    max={50000}
                    step={1000}
                    valueLabelDisplay="auto"
                    marks={[
                      { value: 10000, label: '10K' },
                      { value: 25000, label: '25K' },
                      { value: 50000, label: '50K' },
                    ]}
                  />
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="outlined" size="small" startIcon={<RefreshIcon />}>
                  Refresh Cache
                </Button>
                <Button variant="outlined" size="small" startIcon={<PowerIcon />}>
                  Optimize DB
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Emergency Action Confirmation Dialog */}
      <Dialog open={!!dialogOpen} onClose={() => setDialogOpen('')}>
        <DialogTitle color="error">
          Confirm Emergency Action
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This action will affect all mobile app users immediately!
          </Alert>
          <Typography variant="body2">
            Are you sure you want to execute "{dialogOpen}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen('')}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => confirmEmergencyAction(dialogOpen)}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MobileAppSettingsPage;